import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OPEN_SEARCH_EVENT } from "@/components/search/GlobalSearch";
import { cn } from "@/lib/cn";
import { useIsAdminPortal } from "@/lib/portal";

interface QuickAction {
  key: string;
  to: string;
  icon: React.ReactNode;
}

const centralActions: QuickAction[] = [
  {
    key: "createSession",
    to: "/sessions/new",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="4" width="12" height="18" rx="2" />
        <path d="M9 4V2h6v2M12 11v6M9 14h6" />
      </svg>
    ),
  },
  {
    key: "createDoctor",
    to: "/doctors/new",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2v6a4 4 0 0 0 8 0V2" />
        <circle cx="18" cy="14" r="2.5" />
        <path d="M10 12v3a6 6 0 0 0 5.5 6" />
      </svg>
    ),
  },
  {
    key: "createReviewer",
    to: "/reviewers/new",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="8" r="3.5" />
        <path d="M3 21v-1a6 6 0 0 1 12 0v1M18 8v6M15 11h6" />
      </svg>
    ),
  },
];

const adminActions: QuickAction[] = [
  {
    key: "sendNotification",
    to: "/admin/notifications",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  },
  {
    key: "viewReports",
    to: "/admin/reports",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="m7 14 4-4 4 4 5-6" />
      </svg>
    ),
  },
  {
    key: "exportData",
    to: "/admin/exports",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
      </svg>
    ),
  },
];

/**
 * Context-aware "new" target for the keyboard shortcut N. Portal comes from the
 * shared source; the remaining checks are route-specific (which create form),
 * not portal detection. [audit P0-3b]
 */
function newTargetFor(pathname: string, isAdmin: boolean): string | null {
  if (isAdmin) return null;
  if (pathname.startsWith("/doctors")) return "/doctors/new";
  if (pathname.startsWith("/reviewers")) return "/reviewers/new";
  return "/sessions/new";
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}

/** Floating quick-actions menu + keyboard shortcuts (roadmap 3.2). */
export function QuickActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const adminPortal = useIsAdminPortal();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const actions = adminPortal ? adminActions : centralActions;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (e.key === "/") {
        e.preventDefault();
        window.dispatchEvent(new Event(OPEN_SEARCH_EVENT));
      } else if (e.key.toLowerCase() === "n") {
        const target = newTargetFor(pathname, adminPortal);
        if (target) {
          e.preventDefault();
          navigate(target);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pathname, navigate, adminPortal]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={ref} className="fixed bottom-16 end-6 z-[70] flex flex-col items-end gap-2">
      {open && (
        <div className="flex flex-col items-stretch gap-1.5">
          {actions.map((action, i) => (
            <button
              key={action.key}
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(action.to);
              }}
              style={{ transitionDelay: `${i * 30}ms` }}
              className="flex items-center gap-2.5 rounded-full border border-line bg-card py-2 pe-4 ps-2.5 text-[12.5px] font-bold text-ink-1 shadow-lift transition-all hover:-translate-y-px hover:border-primary/40"
            >
              <span className="grid h-7 w-7 place-items-center rounded-full bg-primary/10 text-primary">{action.icon}</span>
              {t(`quickActions.${action.key}`)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              window.dispatchEvent(new Event(OPEN_SEARCH_EVENT));
            }}
            className="flex items-center gap-2.5 rounded-full border border-line bg-card py-2 pe-4 ps-2.5 text-[12.5px] font-bold text-ink-1 shadow-lift transition-all hover:-translate-y-px hover:border-primary/40"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-indigo/10 text-indigo">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </span>
            {t("quickActions.search")}
            <kbd className="ms-auto rounded border border-line px-1.5 text-[10px] text-ink-3">/</kbd>
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("quickActions.title")}
        aria-expanded={open}
        data-testid="quick-actions-fab"
        className={cn(
          "grid h-13 w-13 place-items-center rounded-full bg-primary text-white shadow-[0_6px_20px_rgb(var(--color-primary)/0.45)] transition-transform hover:scale-105",
          open && "rotate-45",
        )}
        style={{ height: 52, width: 52 }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </button>
    </div>
  );
}
